import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as echarts from "echarts";
import { DatePicker, message, Spin, Skeleton } from "antd";
import api from "@/api";
import { useSelectDate } from "@/hooks/useDate";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
dayjs.locale("zh-cn");

import dataJson from "@/../public/data.json";

const prefix = "analysis";
import "@/pages/appDetail/analysis.less";
import { cloneDeep } from "lodash-es";

const { RangePicker } = DatePicker;
const dateFormat = "yyyy-MM-dd";
const rangeDateFormat = "YYYY-MM-DD";

function MyAiModelUserBoard() {
  const { resources, two_bars } = dataJson;

  const [active, setActive] = useState<number>(1);
  const [openData, setOpenData] = useState<any>({});
  const [useData, setUseData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [isOpenErr, setIsOpenErr] = useState(true);
  const [isUseErr, setIsUseErr] = useState(true);
  const openChartRef = useRef(null);
  const useChartRef = useRef(null);
  const { selectDate, setSelectDate, disabledDate } = useSelectDate();

  const { id } = useParams();

  const options: any = {
    title: {
      text: "",
      textStyle: {
        color: "#191919",
        fontSize: 16,
      },
    },
    tooltip: {
      trigger: "axis",
    },
    xAxis: {
      type: "category",
      axisTick: {
        show: false,
      },
      data: [],
    },
    yAxis: {
      type: "value",
      splitLine: {
        lineStyle: {
          color: "#EEEEEE",
        },
      },
    },
    grid: {
      left: 46,
      right: 10,
      bottom: 30,
      top: 40,
    },
    series: [
      {
        data: [],
        type: "line",
        symbol: "none",
        smooth: true,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(109,143,240,0.3)" },
            { offset: 1, color: "rgba(0,125,255,0.1)" },
          ]),
        },
        lineStyle: { color: "#6D8FF0" },
        areaStyle: {},
      },
    ],
    graphic: {
      type: "text",
      left: "center",
      top: "middle",
      silent: true,
      invisible: true,
      style: {
        fill: "#9d9d9d",
        fontWeight: "bold",
        text: "暂无数据",
        fontFamily: "Microsoft YaHei",
        fontSize: "25px",
      },
    },
  };

  const handleError = (chartDom: any) => {
    const optionsCopy = cloneDeep(options);
    optionsCopy.graphic.invisible = false;
    const chart: any = chartDom && echarts.init(chartDom);
    chart && chart.setOption(optionsCopy);
  };

  const getOpen = async () => {
    try {
      setLoading(true);
      const res: any = await api.getOpenData({
        timeRangeType: "customize",
        timeRange: {
          startTime: new Date(Number(selectDate[0])).getTime(),
          endTime: new Date(Number(selectDate[1])).getTime(),
        },
        timeFormat: dateFormat,
        appId: id,
      });

      if (res?.code == 200) {
        setOpenData(res.data);
        const optionsCopy = cloneDeep(options);
        optionsCopy.title.text = resources.open_title;
        optionsCopy.xAxis.data = res.data.times;
        optionsCopy.series[0].data = res.data.values || [];
        optionsCopy.graphic.invisible = optionsCopy.series[0].data?.length > 0;
        const openChart: any = openChartRef.current && echarts.init(openChartRef.current);
        openChart && openChart.setOption(optionsCopy);
        setLoading(false);
        setIsOpenErr(false);
      } else {
        setLoading(false);
        setIsOpenErr(true);
        handleError(openChartRef.current);
        message.error(res.msg);
      }
    } catch (errorInfo: any) {
      setLoading(false);
      setIsOpenErr(true);
      handleError(openChartRef.current);
      console.error(errorInfo);
    }
  };
  const getUse = async () => {
    try {
      setLoading(true);
      const res: any = await api.getUseData({
        timeRangeType: "customize",
        timeRange: {
          startTime: new Date(Number(selectDate[0])).getTime(),
          endTime: new Date(Number(selectDate[1])).getTime(),
        },
        timeFormat: dateFormat,
        appId: id,
      });

      if (res?.code == 200) {
        setUseData(res.data);
        const optionsCopy = cloneDeep(options);
        optionsCopy.title.text = resources.use_title;
        optionsCopy.xAxis.data = res.data.times;
        optionsCopy.series[0].data = res.data.values || [];
        optionsCopy.graphic.invisible = optionsCopy.series[0].data?.length > 0;
        const useChart: any = useChartRef.current && echarts.init(useChartRef.current);
        useChart && useChart.setOption(optionsCopy);
        setLoading(false);
        setIsUseErr(false);
      } else {
        setLoading(false);
        setIsUseErr(true);
        handleError(useChartRef.current);
        message.error(res.msg);
      }
    } catch (errorInfo: any) {
      setLoading(false);
      setIsUseErr(true);
      handleError(useChartRef.current);
      console.error(errorInfo);
    }
  };

  useEffect(() => {
    getOpen();
    getUse();
  }, [active, selectDate, id]);

  return (
    <div className={prefix}>
      <Spin spinning={loading} size="large">
        <div className={`${prefix}-top clearfix`}>
          <div
            className={`${prefix}-card ${active === 1 ? "active" : ""}`}
            onClick={() => {
              setActive(1);
            }}
          >
            <Skeleton active loading={isOpenErr}>
              <div className={`${prefix}-card-title`}>{resources.open_title}</div>
              <div className={`${prefix}-card-content`}>
                <div className={`${prefix}-card-num`}>{openData?.totalValue ?? two_bars}</div>
                <div className={`${prefix}-card-content-right`}>
                  <div className={`${prefix}-card-contrastText`}>{resources.contrast}</div>
                  <div className={`${prefix}-card-content-right-bottom`}>
                    <div className={`${prefix}-card-contrastNum`}>
                      {openData?.lastPeriodIsZero
                        ? two_bars
                        : Number(openData?.growthRate) > 0
                        ? `${resources.increase}${Math.abs(Number(openData?.growthRate))}%`
                        : `${resources.reduce}${Math.abs(Number(openData?.growthRate))}%`}
                    </div>
                    {!openData?.lastPeriodIsZero ? (
                      <img
                        src={Number(openData?.growthRate) > 0 ? resources.increase_icon : resources.reduce_icon}
                        alt=""
                        className={`${prefix}-card-icon`}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </Skeleton>
          </div>

          <div
            className={`${prefix}-card ${active === 2 ? "active" : ""}`}
            onClick={() => {
              setActive(2);
            }}
          >
            <Skeleton active loading={isUseErr}>
              <div className={`${prefix}-card-title`}>{resources.use_title}</div>
              <div className={`${prefix}-card-content`}>
                <div className={`${prefix}-card-num`}>{useData?.totalValue ?? two_bars}</div>
                <div className={`${prefix}-card-content-right`}>
                  <div className={`${prefix}-card-contrastText`}>{resources.contrast}</div>
                  <div className={`${prefix}-card-content-right-bottom`}>
                    <div className={`${prefix}-card-contrastNum`}>
                      {useData?.lastPeriodIsZero
                        ? two_bars
                        : Number(useData?.growthRate) > 0
                        ? `${resources.increase}${Math.abs(Number(useData?.growthRate))}%`
                        : `${resources.reduce}${Math.abs(Number(useData?.growthRate))}%`}
                    </div>
                    {!useData?.lastPeriodIsZero ? (
                      <img
                        src={Number(useData?.growthRate) > 0 ? resources.increase_icon : resources.reduce_icon}
                        alt=""
                        className={`${prefix}-card-icon`}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </Skeleton>
          </div>
          <RangePicker
            value={selectDate}
            allowClear={false}
            disabledDate={disabledDate}
            format={rangeDateFormat}
            className={`${prefix}-rangePicker`}
            onChange={(val: any) => {
              setSelectDate(val === null ? [] : val);
            }}
          />
        </div>
        {active === 1 ? (
          <div ref={openChartRef} className={`${prefix}-chart`} />
        ) : (
          <div ref={useChartRef} className={`${prefix}-chart`} />
        )}
      </Spin>
    </div>
  );
}

export default MyAiModelUserBoard;
